// src/pages/users/Usuarios.tsx
import { useEffect, useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPen, faUsers, faUsersViewfinder, faUsersBetweenLines, faUsersSlash } from '@/icons';
import Modal from '../../components/common/Modal';
import SidePanel from '../../components/common/SidePanel';
import SearchUserModalContent from '../users/SearchUserModalContent';
import UserFormSidePanelContent from './UserFormSidePanelContent';
import KanbanColumn from '../../components/common/KanbanColumn';
import KanbanCard from '../../components/common/KanbanCard';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFormFormatting } from '../../hooks/useFormFormatting';
import { Pessoa, PessoaIntegration, PermissionsMap } from '../../types/usuarios/Pessoa';
import { UsuarioParametrosAprovacaoUpsert } from '../../types/aprovacao/Aprovacao';
import '../../pages/users/Usuarios.css';
import { formatWhatsapp, formatCpf, maskCpf } from '../../utils/formatUtils';
import IconSearchButton from '../../components/common/IconSearchButton';
import IconResetButton from '../../components/common/IconResetButton';
import { useUsuariosKanban } from '../../hooks/useUsuariosKanban';
import { useUsuarioMutations } from '../../hooks/useUsuarioMutations';

const Usuarios = () => {
  const { theme } = useTheme();
  const { permissoes, role, hasPermission } = useAuth();
  // Gate UX da sub-secao "Parametros de aprovacao" no side panel de edicao.
  // Backend (a01c2ea/1a8e863) gateia GET/PUT /v1/permissoes/{id}/parametros-aprovacao
  // por usuarios.editar_parametros_aprovacao. MASTER bypassa (premissa #8).
  const podeEditarParametrosAprovacao =
    role === 'ROLE_MASTER' || hasPermission('usuarios.editar_parametros_aprovacao');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const integrationSentinelRef = useRef<HTMLDivElement>(null);
  const activeSentinelRef = useRef<HTMLDivElement>(null);
  const inactiveSentinelRef = useRef<HTMLDivElement>(null);

  const kanbans = useUsuariosKanban();

  const handleReload = () => {
    kanbans.reloadAllColumns();
    setIsSearchActive(false);
  };

  const mutations = useUsuarioMutations({ role, hasPermission, onReload: handleReload });

  const {
    fields,
    errors,
    handleChange,
    handleBlur,
    isFormValid,
    getFormattedPerson,
    setFields,
  } = useFormFormatting(mutations.selectedPerson);

  const handleFormSubmit = (
    permissions?: PermissionsMap,
    approverParams?: UsuarioParametrosAprovacaoUpsert | null
  ) =>
    mutations.handleSave(permissions, approverParams ?? null, { getFormattedPerson, isFormValid });

  useEffect(() => {
    document.title = 'Bluelt | Usuários';
  }, []);

  useEffect(() => {
    if (mutations.showSidePanel) {
      mutations.loadLojasAcesso();
      if (mutations.selectedPerson) {
        const whatsappFormatted = mutations.selectedPerson.contato?.whatsapp
          ? formatWhatsapp(mutations.selectedPerson.contato.whatsapp)
          : '+55 ';
        setFields({
          nome: mutations.selectedPerson.nome || '',
          cpf: formatCpf(mutations.selectedPerson.cpf || ''),
          whatsapp: whatsappFormatted,
          email: mutations.selectedPerson.contato?.email || '',
          userName: mutations.selectedPerson.usuario?.userName || '',
          codUsuarioIntegracao: mutations.selectedPerson.usuario?.codUsuarioIntegracao?.toString(),
          role: mutations.selectedPerson?.usuario?.role || 'ROLE_USER',
        });
        mutations.setSelectedLojasIds(mutations.selectedPerson.lojasAcesso?.map((loja) => loja.id) || []);
      }
    }
  }, [mutations.showSidePanel, mutations.selectedPerson, setFields]);

  useEffect(() => {
    if (kanbans.integration.items.length === 0 || isSearchActive) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          kanbans.integration.loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = integrationSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.integration.loadMore, kanbans.integration.items, isSearchActive]);

  useEffect(() => {
    if (kanbans.active.items.length === 0 || isSearchActive) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          kanbans.active.loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = activeSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.active.loadMore, kanbans.active.items, isSearchActive]);

  useEffect(() => {
    if (kanbans.inactive.items.length === 0 || isSearchActive) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          kanbans.inactive.loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = inactiveSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.inactive.loadMore, kanbans.inactive.items, isSearchActive]);

  const handleOpenSearchModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleResetSearch = () => {
    handleReload();
  };

  const handleSearchResults = (result: {
    ativos: Pessoa[];
    inativos: Pessoa[];
    integracao: PessoaIntegration[];
  }) => {
    kanbans.applySearchResults(result);
    setIsSearchActive(true);
    setIsModalOpen(false);
  };

  const renderIntegrationItem = useCallback((pessoa: PessoaIntegration) => {
    // Valida CPF (menos de 11 dígitos ou todos dígitos iguais), considerando espaços vazios como inválido
    const cpfRaw = pessoa.cpf_cnpj || ''; // Garante que seja string, mesmo se null/undefined
    const cpfDigits = cpfRaw.trim().replace(/\D/g, ''); // Remove espaços e não-dígitos
    const isCpfInvalidLength = cpfDigits.length < 11;
    const isCpfAllSame = cpfDigits.length === 11 && /^(\d)\1{10}$/.test(cpfDigits); // Verifica se todos dígitos são iguais
    const isCpfInvalid = isCpfInvalidLength || isCpfAllSame;

    // Valida loja vinculada (vazia, nula, ou apenas espaços)
    const lojaRaw = pessoa.nomeFantasiaFilial || ''; // Garante que seja string, mesmo se null/undefined
    const isLojaInvalid = lojaRaw.trim() === '';

    // isValid é true apenas se NENHUMA das condições for inválida
    const isValid = !isCpfInvalid && !isLojaInvalid;

    return (
      <KanbanCard<PessoaIntegration>
        item={pessoa}
        renderContent={(integ) => (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <strong>{integ.nome}</strong>
            <div className="kanban-item-force-integration-card-itens">
              CPF: {isCpfInvalid ? 'Inválido!' : maskCpf(integ.cpf_cnpj)}
            </div>
            <div className="kanban-item-force-integration-card-itens">
              Loja Vinculada: {isLojaInvalid ? 'Erro de Integração' : integ.nomeFantasiaFilial}
            </div>
          </div>
        )}
        isDraggable={true}
        onDragStart={mutations.onDragStartIntegration}
        isValid={isValid} // Aplica estilização condicional
      />
    );
  }, [mutations.onDragStartIntegration]);

  const renderActiveItem = useCallback((pessoa: Pessoa) => (
    <KanbanCard<Pessoa>
      item={pessoa}
      renderContent={(p) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <strong>{p.nome}</strong>
          CPF: {maskCpf(p.cpf)}
          {p.usuario && (
            <>
              <div>UserName: {p.usuario.userName}</div>
              <div>Loja Vinculada Integração: {p.usuario.nomeFilial || 'Nenhuma'}</div>
            </>
          )}
        </div>
      )}
      isDraggable={true}
      onDragStart={mutations.onDragStartActive}
      actionButton={
        role === 'ROLE_MASTER' || permissoes?.usuarios?.editar
          ? {
              icon: faUserPen,
              onClick: mutations.handleEditClick,
              title: 'Editar Usuário',
              className: 'edit-button',
            }
          : undefined
      }
    />
  ), [mutations.onDragStartActive, mutations.handleEditClick, role, permissoes]);

  const renderInactiveItem = useCallback((pessoa: Pessoa) => (
    <KanbanCard<Pessoa>
      item={pessoa}
      renderContent={(p) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <strong>{p.nome}</strong>
          CPF: {maskCpf(p.cpf)}
          {p.usuario && (
            <>
              <div>UserName: {p.usuario.userName}</div>
              <div>Loja Vinculada: {p.usuario.nomeFilial || 'Nenhuma'}</div>
            </>
          )}
        </div>
      )}
      isDraggable={true}
      onDragStart={mutations.onDragStartInactive}
    />
  ), [mutations.onDragStartInactive]);

  return (
    <div className={`${theme}`}>
      <main className="usuarios-main">
        <h1>
          <FontAwesomeIcon icon={faUsers} /> Gestão de Usuários
        </h1>
          <div className="header-actions">
            {isSearchActive && (
              <IconResetButton
                onClick={handleResetSearch}
                title="Limpar Pesquisa"
              />
            )}
            {(role === 'ROLE_MASTER' || hasPermission('usuarios.listar')) && (
              <IconSearchButton
                onClick={handleOpenSearchModal}
                title="Procurar Usuário"
              />
            )}
          </div>
      </main>
      <div className="usuarios-content">
        <div className="kanban-container" style={{ width: '100%' }}>
          <KanbanColumn<PessoaIntegration>
            title="Integração"
            icon={faUsersViewfinder}
            data={kanbans.integration.items}
            renderItem={renderIntegrationItem}
            onDragOver={mutations.onDragOver}
            onDrop={(e) => mutations.onDrop(e, 'integration')}
            ref={integrationSentinelRef}
            showCountCircle={true}
            totalCount={kanbans.integration.totalItems}
          />
          <KanbanColumn<Pessoa>
            title="Ativos"
            icon={faUsersBetweenLines}
            data={kanbans.active.items}
            renderItem={renderActiveItem}
            onDragOver={mutations.onDragOver}
            onDrop={(e) => mutations.onDrop(e, 'active')}
            ref={activeSentinelRef}
            showCountCircle={true}
            totalCount={kanbans.active.totalItems}
          />
          <KanbanColumn<Pessoa>
            title="Inativos"
            icon={faUsersSlash}
            data={kanbans.inactive.items}
            renderItem={renderInactiveItem}
            onDragOver={mutations.onDragOver}
            onDrop={(e) => mutations.onDrop(e, 'inactive')}
            ref={inactiveSentinelRef}
            showCountCircle={true}
            totalCount={kanbans.inactive.totalItems}
          />
        </div>
        <SidePanel isOpen={mutations.showSidePanel} onClose={mutations.handleCancelForm} theme={theme}>
          {mutations.selectedPerson && (
            <UserFormSidePanelContent
              selectedPerson={mutations.selectedPerson}
              fields={fields}
              errors={errors}
              lojasAcesso={mutations.lojasAcesso}
              selectedLojasIds={mutations.selectedLojasIds}
              isLoading={mutations.isLoading}
              handleChange={handleChange}
              handleBlur={handleBlur}
              handleLojaChange={mutations.handleLojaChange}
              handleSave={handleFormSubmit}
              handleCancel={mutations.handleCancelForm}
              isFormValid={isFormValid}
              theme={theme}
              podeEditarParametrosAprovacao={podeEditarParametrosAprovacao}
            />
          )}
        </SidePanel>
      </div>
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} theme={theme}>
        <SearchUserModalContent theme={theme} onSearch={handleSearchResults} />
      </Modal>
    </div>
  );
};

export default Usuarios;